/**
 * CetusEd NextGen — Session context.
 *
 * Bridges the framework-free session store into React via useSyncExternalStore,
 * and kicks off the one boot-time restore. Everything protected reads
 * `useSession().status`; ONLY `authenticated` is allowed to render protected
 * content (⛔2 fail closed — see ProtectedRoute).
 */
import { createContext, useContext, useEffect, useRef, useSyncExternalStore } from 'react'
import type { ReactNode } from 'react'
import { AppError } from '@cetused/shared'
import { sessionStore } from './session-store'
import { restoreSession } from './session-controller'
import type { SessionSnapshot } from './types'

const SessionContext = createContext<SessionSnapshot | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const snapshot = useSyncExternalStore(
    sessionStore.subscribe,
    sessionStore.getSnapshot,
    sessionStore.getSnapshot,
  )

  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    // Fire-and-forget: restoreSession drives status through the store, which
    // re-renders this provider. It never throws (fail-closed inside).
    void restoreSession()
  }, [])

  return <SessionContext.Provider value={snapshot}>{children}</SessionContext.Provider>
}

/** Read the current session. Throws if used outside <SessionProvider>. */
export function useSession(): SessionSnapshot {
  const ctx = useContext(SessionContext)
  if (!ctx) {
    throw AppError.internal('useSession must be used within <SessionProvider>')
  }
  return ctx
}
